import React from 'react';

/**
 * StaticSEOContent - Unique static HTML per route for crawlers.
 * Renders inside <noscript> so Google can read content even without JS.
 * Each route gets a unique H1 + introductory paragraph to avoid duplicate content.
 */

interface StaticSEOContentProps {
  pageType?: string;
  /** Custom H1 override for dynamic pages (blog posts, etc.) */
  customH1?: string;
  /** Custom description override */
  customDescription?: string;
}

const PAGE_CONTENT: Record<string, { h1: string; description: string }> = {
  home: {
    h1: 'AI Project Management Software — 100% Free for Teams Worldwide',
    description: 'ProyectMind is the most complete AI project management software. Interactive Gantt charts, predictive analytics, task automation, hybrid methodologies and real-time dashboards. 100% free, with no limits on users or projects. Also available in Spanish for teams in Mexico and LATAM.'
  },
  features: {
    h1: 'Funcionalidades de ProyectMind: IA, Gantt y Kanban Gratis',
    description: 'Explora todas las herramientas de ProyectMind: IA nativa para predicción de riesgos, diagrama Gantt interactivo, tableros Kanban, automatización de workflows, reportes PDF automáticos y gestión de equipos en tiempo real. Todo incluido gratis sin límites.'
  },
  pricing: {
    h1: 'Planes ProyectMind — Siempre Gratis, Sin Límites',
    description: 'ProyectMind es completamente gratis: usuarios ilimitados, proyectos ilimitados, IA incluida. Sin planes de pago ocultos ni tarjeta de crédito requerida. La herramienta de gestión de proyectos más accesible de LATAM.'
  },
  blog: {
    h1: 'Blog de ProyectMind: Productividad, IA y Gestión de Proyectos',
    description: 'Artículos, guías y mejores prácticas sobre gestión de proyectos con inteligencia artificial. Aprende metodologías ágiles, automatización, análisis predictivo y productividad para equipos en México y LATAM.'
  },
  integrations: {
    h1: 'Integraciones de ProyectMind con tus Herramientas',
    description: 'Conecta ProyectMind con Slack, Google Drive, GitHub, Microsoft Teams, Jira y más. Automatiza tu flujo de trabajo e integra tus herramientas favoritas sin costo adicional.'
  },
  'vs-monday': {
    h1: 'ProyectMind vs Monday.com: La Alternativa Gratuita con IA',
    description: 'Compara ProyectMind con Monday.com. Mismas funciones de gestión de proyectos — Gantt, automatización, reportes — pero 100% gratis. Monday cobra desde $11 USD/usuario/mes. ProyectMind incluye IA predictiva sin costo.'
  },
  'vs-asana': {
    h1: 'ProyectMind vs Asana: Gestión de Proyectos Gratis en Español',
    description: 'ProyectMind ofrece todo lo que Asana tiene — y más — sin costo. IA predictiva incluida, Gantt interactivo, automatización ilimitada y soporte nativo en español para equipos en LATAM.'
  },
  'vs-trello': {
    h1: 'ProyectMind vs Trello: Kanban + Gantt + IA, Todo Gratis',
    description: 'Trello es un tablero Kanban simple, pero limitado para gestión de proyectos seria. ProyectMind incluye Kanban + Gantt + IA predictiva + automatización, todo 100% gratis.'
  },
  'vs-clickup': {
    h1: 'ProyectMind vs ClickUp: Sin Límites y Sin Costo',
    description: 'ClickUp intenta hacer todo pero abruma. ProyectMind es potente y simple, con IA predictiva real, Gantt interactivo y automatización, todo 100% gratis y fácil de usar.'
  },
  'vs-jira': {
    h1: 'ProyectMind vs Jira: Alternativa Gratis y Más Fácil',
    description: 'Jira es poderoso para desarrollo pero complejo y costoso. ProyectMind ofrece gestión completa de proyectos con IA predictiva, Gantt y automatización, 100% gratis y accesible para todo el equipo.'
  },
  'vs-notion': {
    h1: 'ProyectMind vs Notion: Gestión de Proyectos Real vs Wiki',
    description: 'Notion es excelente para documentación pero no es un software de gestión de proyectos real. ProyectMind ofrece Gantt, Scrum, Kanban, IA predictiva y automatización, todo 100% gratis.'
  },
  'gestion-proyectos-pymes-mexico': {
    h1: 'Software de Gestión de Proyectos para PYMEs en México — 100% Gratis',
    description: 'La mejor herramienta de gestión de proyectos para equipos mexicanos. IA en español, Gantt gratis, sin costo por usuario. Diseñado para el contexto LATAM.'
  },
  'diagrama-gantt': {
    h1: 'Diagrama Gantt Gratis Online — Sin Registro, Sin Límites',
    description: 'Crea diagramas Gantt interactivos gratis con ProyectMind. Arrastra y suelta tareas, define dependencias, visualiza el camino crítico y gestiona tus proyectos con IA predictiva.'
  },
  'alternativa-monday': {
    h1: 'Alternativa a Monday.com Gratis — Todas las Funciones, Cero Costo',
    description: 'ProyectMind es la alternativa gratuita a Monday.com con IA predictiva, Gantt interactivo, automatización y usuarios ilimitados. $0 vs $11 USD/usuario/mes.'
  }
};

const StaticSEOContent: React.FC<StaticSEOContentProps> = ({ 
  pageType = 'home',
  customH1,
  customDescription
}) => {
  const content = PAGE_CONTENT[pageType];
  const h1 = customH1 || content?.h1;
  const description = customDescription || content?.description;

  if (!h1) return null;

  return (
    <noscript>
      <article 
        className="max-w-4xl mx-auto px-4 py-8"
        itemScope 
        itemType="https://schema.org/WebPage"
      >
        <div itemProp="mainContentOfPage">
          <h1>{h1}</h1>
          <p>{description}</p>
        </div>
        <footer>
          <p>
            <strong>ProyectMind</strong> - Software de Gestión de Proyectos con IA<br />
            100% Gratis para México y Latinoamérica<br />
            <a href="https://proyectmind.online">https://proyectmind.online</a>
          </p>
        </footer>
      </article>
    </noscript>
  );
};

export default StaticSEOContent;
